import React, { useState, useEffect } from 'react';
import {
  TextField, Button, MenuItem, Select, FormControl, InputLabel,
  Avatar, Box, Typography, IconButton, Chip, Checkbox, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Menu, Autocomplete,
  FormHelperText, useTheme, List, ListItem, ListItemButton, Divider, Grid, Paper, alpha
} from '@mui/material';
import {
  PhotoCamera, HelpOutline, Edit, Clear,
  Add as AddIcon, Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  LocalHospital as MedicalIcon,
  Straighten as StraightenIcon,
  FolderOpen as FolderOpenIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useClubTheme } from '../contexts/ClubThemeContext';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { useNavigate } from 'react-router-dom';
import { usePositions } from '../utils/positions';

// === Imports des deux Select personnalisés ===
import SelectSelectionNational from '../components/SelectSelectionNational.js';
import NationalitiesSelect from '../components/NationalitiesSelect';
import NationalitiesSelect2 from '../components/NationalitiesSelect2';
import YearChips from '../components/YearChips';

// Composant pour capturer une photo
const PhotoCapture = ({ onCapture, t }) => {
  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Redimensionnement de l'image avant l'envoi
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          const MAX_DIMENSION = 800;
          if (width > height && width > MAX_DIMENSION) {
            height = height * (MAX_DIMENSION / width);
            width = MAX_DIMENSION;
          } else if (height > MAX_DIMENSION) {
            width = width * (MAX_DIMENSION / height);
            height = MAX_DIMENSION;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            onCapture(resizedFile);
          }, 'image/jpeg', 0.8);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box>
      <input
        accept="image/*"
        id="photo-capture"
        type="file"
        capture="environment"
        onChange={handlePhotoCapture}
        style={{ display: 'none' }}
      />
      <label htmlFor="photo-capture">
        <Button
          variant="contained"
          component="span"
          startIcon={<PhotoCamera />}
          fullWidth
        >
          {t('createPlayer.photo.takePhoto')}
        </Button>
      </label>
    </Box>
  );
};

// Composant pour sélectionner une photo depuis la bibliothèque
const PhotoSelection = ({ onSelect, t }) => {
  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Même logique de redimensionnement que dans PhotoCapture
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          const MAX_DIMENSION = 800;
          if (width > height && width > MAX_DIMENSION) {
            height = height * (MAX_DIMENSION / width);
            width = MAX_DIMENSION;
          } else if (height > MAX_DIMENSION) {
            width = width * (MAX_DIMENSION / height);
            height = MAX_DIMENSION;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            onSelect(resizedFile);
          }, 'image/jpeg', 0.8);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Box>
      <input
        accept="image/*"
        id="photo-select"
        type="file"
        onChange={handlePhotoSelect}
        style={{ display: 'none' }}
      />
      <label htmlFor="photo-select">
        <Button
          variant="contained"
          component="span"
          startIcon={<Edit />}
          fullWidth
        >
          {t('createPlayer.photo.selectPhoto')}
        </Button>
      </label>
    </Box>
  );
};

// Composant pour le dialogue de modification de photo
const PhotoEditDialog = ({ open, onClose, onCapture, onSelect, t }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        {t('createPlayer.photo.changePhoto')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <PhotoCapture onCapture={(file) => {
            onCapture(file);
            onClose();
          }} t={t} />
          <PhotoSelection onSelect={(file) => {
            onSelect(file);
            onClose();
          }} t={t} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

// Clés des rôles défensifs (pour traduction)
const DEFENSIVE_ROLE_KEYS = [
  'intenseOneOnOne',
  'perimeterDefender',
  'wingStopper',
  'switchDefender',
  'anchorBig',
  'defensiveRebounder',
  'rimProtector',
  'pointOfAttack',
  'slowLaterally',
  'nonDefender'
];

// Clés des attributs JE (Juego Exterior) pour traduction
const JE_ATTRIBUTE_KEYS = [
  'oneOnOne',
  'threeLevelScorer',
  'offBallActivity',
  'athletic',
  'pickAndRollAbility',
  'drawFoulsAbility',
  'clutch',
  'tough',
  'energy',
  'wingspan',
  'physical',
  'hustle',
  'iqVision',
  'lowPostGame',
  'paintTouches',
  'slasher',
  'points',
  'firstStep',
  'consistentProduction',
  'rebounding',
  'talent',
  'shooter',
  'catchAndShoot',
  'cornerThree',
  'pullUpShooting',
  'transitionPace',
  'blueCollar',
  'speedAgility',
  'versatility',
  'verticality'
];

// Clés des attributs JI (Juego Interior) pour traduction
const JI_ATTRIBUTE_KEYS = [
  'oneOnOne',
  'offBallActivity',
  'athletic',
  'screenRollFinish',
  'effectiveScreens',
  'softTouch',
  'clutch',
  'tough',
  'energy',
  'wingspan',
  'footwork',
  'physical',
  'hustle',
  'iqVision',
  'postFaceUpGame',
  'lobThreat',
  'points',
  'consistentProduction',
  'rimProtector',
  'rebounding',
  'rimRunner',
  'talent',
  'shooter',
  'catchAndShoot',
  'cornerPopThree',
  'pullUpShooting',
  'blueCollar',
  'transitionPace',
  'multipleEfforts',
  'speedAgility',
  'versatility',
  'verticality'
];

// On définit la liste de propositions pour "Rango Salarial"
const priceRanges = [
  "0-50k€",
  "50-100k€",
  "100-150k€",
  "150-200k€",
  "200-250k€",
  "250-300k€",
  "300-350k€",
  "350-400k€",
  "400-450k€",
  "450-500k€",
  "500-550k€",
  "550-600k€",
  "600-650k€",
  "650-700k€",
  "700-750k€",
  "750-800k€",
  "800-850k€",
  "850-900k€",
  "900-950k€",
  "950k€-1M€",
  "1M€-1.2M€",
  "1.2M€-1.4M€",
  "1.4M€-1.6M€",
  "1.6M€-1.8M€",
  "1.8M€-2M€",
  "2M€-2.2M€",
  "2.2M€-2.4M€",
  "2.4M€-2.5M€"
];

// On définit la liste de propositions pour "Contrato"
const contratos = [
  "2-Way",
  "Exhibit-10",
  "1 año",
  "1+1",
  "2 años",
  "3 años",
  "On Loan"
];

// *** Nouveau champ ***: Buyout
const buyoutOptions = [
  "0-50k€",
  "50-100k€",
  "100-150k€",
  "150-200k€",
  "200-250k€",
  "250-300k€",
  "300-350k€",
  "350-400k€",
  "400-450k€",
  "450-500k€",
  "500-550k€",
  "550-600k€",
  "600-650k€",
  "650-700k€",
  "700-750k€",
  "750-800k€",
  "800-850k€",
  "850-900k€",
  "900-950k€",
  "950k€-1M€",
  "1M€-1.2M€",
  "1.2M€-1.4M€",
  "1.4M€-1.6M€",
  "1.6M€-1.8M€",
  "1.8M€-2M€",
  "2M€-2.2M€",
  "2.2M€-2.4M€",
  "2.4M€-2.5M€"
];

// *** Nouveau champ ***: Origen Genético (clés pour traduction)
const GENETIC_ORIGIN_KEYS = [
  'subsaharianAfrican',
  'afroCaribbean',
  'balkans',
  'westernEurope',
  'latinAmerican',
  'northAfrican',
  'northAmerican'
];

// Clés des profils offensifs (pour traduction)
const OFFENSIVE_ROLE_KEYS = {
  guard: ['generatorWithBall', 'secondaryGeneratorWithBall', 'dominantScorer', 'floorGeneral', 'shootingSpecialist'],
  wing: ['creatorWing', 'versatileWing', 'strongWing', 'slasherWing', 'spotUpWing', 'threeAndD'],
  forward: ['traditionalPowerForward', 'skilledForward', 'stretchFour'],
  big: ['creatorCenter', 'versatileCenter', 'midRangeCenter', 'stretchFive', 'postScorer', 'rimFinisher', 'rollMan', 'marginalOffensiveRole']
};

// Clés des niveaux VBC (pour traduction)
const VBC_LEVEL_KEYS = [
  'lowLevelProspect', 'midLevelProspect', 'goodLevelProspect', 'topProspect', 'futureStar',
  'ebaLow', 'ebaHigh', 'lebLow', 'lebHigh', 'acbRotation',
  'bottomBclEurocup', 'midBclEurocup', 'topBclEurocup',
  'elMid', 'elTop', 'nbaRotation', 'nbaAllStar',
  'collegeLow', 'collegeMid', 'collegeTop'
];

const CrearJugador = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { clubName } = useClubTheme();
  const { positionsWithKeys } = usePositions(t);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  // --- État pour détecter si le formulaire a été modifié (dirty) ---
  const [isDirty, setIsDirty] = useState(false);
  const markDirty = () => {
    setIsDirty(true);
  };

  /**
   * GESTION DU BLOCKAGE DE NAVIGATION :
   * - beforeunload (fermeture / reload)
   * - popstate (bouton retour)
   * - pushState / replaceState (navigation interne via React Router)
   */
  useEffect(() => {
    // -------- 1) BLOQUER LA FERMETURE / RELOAD (beforeunload) --------
    const handleBeforeUnload = (event) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = t('createPlayer.unsavedChanges');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // -------- 2) BLOQUER LE BOUTON RETOUR (popstate) --------
    const handlePopState = (event) => {
      if (isDirty) {
        const confirmLeave = window.confirm(t('createPlayer.unsavedChanges'));
        if (!confirmLeave) {
          // Annule l'action de retour en "repoussant" l'historique
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);

    // -------- 3) BLOQUER LES NAVIGATIONS INTERNES (pushState / replaceState) --------
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const confirmNavigation = () => {
      if (!isDirty) return true;
      return window.confirm(t('createPlayer.unsavedChanges'));
    };

    window.history.pushState = function (...args) {
      if (confirmNavigation()) {
        return originalPushState.apply(this, args);
      }
      return null;
    };

    window.history.replaceState = function (...args) {
      if (confirmNavigation()) {
        return originalReplaceState.apply(this, args);
      }
      return null;
    };

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [isDirty]);

  // Champs existants
  const [nombre, setNombre] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [altura, setAltura] = useState('');
  const [peso, setPeso] = useState('');
  const [puesto, setPuesto] = useState('');
  const [tipo, setTipo] = useState('');
  const [grupos, setGrupos] = useState([]);
  const [selectedGrupos, setSelectedGrupos] = useState([]);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  // Nouveaux champs
  const [nacionalidad, setNacionalidad] = useState('');
  const [segundaNacionalidad, setSegundaNacionalidad] = useState('');
  const [seleccionNacional, setSeleccionNacional] = useState('');
  const [jeJiType, setJeJiType] = useState('');
  const [jeJiAttributes, setJeJiAttributes] = useState([]);
  const [mano, setMano] = useState('');
  const [rolOfensivo, setRolOfensivo] = useState([]);
  const [rolDefensivo, setRolDefensivo] = useState([]);
  const [pasaporte, setPasaporte] = useState('');   // <-- Licencia
  const [estatus, setEstatus] = useState('');
  const [equipo, setEquipo] = useState('');
  const [liga2, setLiga2] = useState('');
  const [equipoPreDraft, setEquipoPreDraft] = useState('');
  const [añoDraft, setAñoDraft] = useState('');
  const [posicionDraft, setPosicionDraft] = useState('');
  const [experienciaAcb, setExperienciaAcb] = useState('');
  const [nbaExperience, setNbaExperience] = useState('');
  const [nivel, setNivel] = useState('');
  const [proyeccion, setProyeccion] = useState('');
  const [agencia, setAgencia] = useState('');
  const [comparacionPro, setComparacionPro] = useState('');
  const [comportamiento, setComportamiento] = useState('');
  const [injury, setInjury] = useState('');
  const [digitalBehaviour, setDigitalBehaviour] = useState('');
  const [rangoPrecios, setRangoPrecios] = useState('');
  const [contrato, setContrato] = useState('');
  const [url, setUrl] = useState('');
  const [pit, setPit] = useState('');
  const [eurocamp, setEurocamp] = useState('');

  // *** Nouveaux champs ajoutés récemment ***
  const [experiencaEuropa, setExperiencaEuropa] = useState('');
  const [shootingRange, setShootingRange] = useState('');

  // *** Nouveau champ ***: SL Vegas
  const [slVegas, setSlVegas] = useState('');

  // *** Nouveau champ ***: Experiencia Fuera de su País
  const [experienciaFuera, setExperienciaFuera] = useState('');

  // *** Nouveau champ ***: Experiencia NCAA
  const [experiencaNcaa, setExperiencaNcaa] = useState('');

  // *** Nouveau champ ***: Origen Genético
  const [origenGenetico, setOrigenGenetico] = useState('');

  // *** Nouveau champ ***: Buyout
  const [buyout, setBuyout] = useState('');

  // *** Nouveau champ ***: Categoría
  const [categoria, setCategoria] = useState('');

  // Nuevos estados para las competiciones
  const [competitionsStructure, setCompetitionsStructure] = useState([]);
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [equipesOptions, setEquipesOptions] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [competitionToDelete, setCompetitionToDelete] = useState(null);
  const [equipeProprietaire, setEquipeProprietaire] = useState(null);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');

  const [createdPlayerId, setCreatedPlayerId] = useState(null);

  // Gestion photo
  const [selectedImage, setSelectedImage] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);

  // Spinner de chargement
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Données backend
  const [teams, setTeams] = useState([]);
  const [leagues2, setLeagues2] = useState([]);
  const [agencies, setAgencies] = useState([]);

  // Menu après création
  const [anchorEl, setAnchorEl] = useState(null);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleMenuItemClick = (type) => {
    if (!createdPlayerId) {
      alert("No hay jugador creado aún.");
      return;
    }
    navigate(`/crear-ficha?playerId=${createdPlayerId}&cardType=${type}`);
    setAnchorEl(null);
    setSuccessModalOpen(false);
  };

  // useEffect : chargement Teams, Leagues, Agencies, Groups
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await api.get('/api/players/teams');
        setTeams(response.data.teams || []);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };

    const fetchLeagues2 = async () => {
      try {
        const response = await api.get('/api/players/leagues2');
        setLeagues2(response.data.leagues2 || []);
      } catch (error) {
        console.error('Error fetching leagues2:', error);
      }
    };

    const fetchAgencies = async () => {
      try {
        const response = await api.get('/api/players/agencies');
        setAgencies(response.data.agencies || []);
      } catch (error) {
        console.error('Error fetching agencies:', error);
      }
    };

    fetchTeams();
    fetchLeagues2();
    fetchAgencies();
  }, [backendUrl, section]);

  useEffect(() => {
    const fetchGrupos = async () => {
      try {
        const response = await api.get('/api/groups');
        setGrupos(response.data);
      } catch (error) {
        console.error("Error al obtener los grupos:", error);
      }
    };
    fetchGrupos();
  }, [backendUrl, section]);

  // Cargar la estructura de competiciones
  useEffect(() => {
    const fetchCompetitionsStructure = async () => {
      try {
        const response = await api.get('/api/players/competitions/structure');
        const structure = response.data.structure || [];
        setCompetitionsStructure(structure);

        // Crear la lista de equipos con sus competiciones
        const equipesMap = {};
        structure.forEach(item => {
          if (!equipesMap[item.equipe_id]) {
            equipesMap[item.equipe_id] = {
              equipe_id: item.equipe_id,
              equipe_nom: item.equipe_nom,
              competitions: []
            };
          }
          // Agregar la competición si no existe ya
          const exists = equipesMap[item.equipe_id].competitions.find(
            c => c.competition_id === item.competition_id
          );
          if (!exists) {
            equipesMap[item.equipe_id].competitions.push({
              niveau_id: item.niveau_id,
              niveau_nom: item.niveau_nom,
              competition_id: item.competition_id,
              competition_nom: item.competition_nom
            });
          }
        });

        // Convertir en array y formatear para mostrar
        const equipesArray = Object.values(equipesMap).map(equipe => ({
          ...equipe,
          display: `${equipe.equipe_nom} (${equipe.competitions.map(c => c.competition_nom).join(', ')})`
        }));

        setEquipesOptions(equipesArray);
      } catch (error) {
        console.error('Error fetching competitions structure:', error);
      }
    };

    fetchCompetitionsStructure();
  }, [backendUrl, section]);

  // Calcul auto de l'année de naissance
  const calcularAñoNacimiento = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).getFullYear().toString();
  };

  // Fonctions pour gérer les compétitions
  const getLogoUrl = (id, type) => {
    const mediaUrl = getMediaBaseUrl();
    return `${mediaUrl}/${type}/${id}.jpg?t=${Date.now()}`;
  };

  const handleOpenDialog = () => {
    setSearchTerm('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSearchTerm('');
  };

  const handleAddEquipe = (equipeOption) => {
    // Ajouter toutes les compétitions de cette équipe
    const newCompetitions = [];
    equipeOption.competitions.forEach(competition => {
      const newCompetition = {
        niveau_id: competition.niveau_id,
        niveau_nom: competition.niveau_nom,
        competition_id: competition.competition_id,
        competition_nom: competition.competition_nom,
        equipe_id: equipeOption.equipe_id,
        equipe_nom: equipeOption.equipe_nom
      };

      // Vérifier si cette combinaison existe déjà
      const exists = selectedCompetitions.find(c =>
        c.niveau_id === newCompetition.niveau_id &&
        c.competition_id === newCompetition.competition_id &&
        c.equipe_id === newCompetition.equipe_id
      );

      if (!exists) {
        newCompetitions.push(newCompetition);
      }
    });

    if (newCompetitions.length > 0) {
      setSelectedCompetitions(prev => [...prev, ...newCompetitions]);
      markDirty();
    }
  };

  const getFilteredEquipes = () => {
    if (!searchTerm) return equipesOptions;
    return equipesOptions.filter(equipe =>
      equipe.equipe_nom.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleSaveCompetitions = () => {
    handleCloseDialog();
  };

  const handleRemoveCompetition = (index, e) => {
    e.stopPropagation();
    setCompetitionToDelete(index);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCompetition = () => {
    if (competitionToDelete !== null) {
      const newCompetitions = [...selectedCompetitions];
      newCompetitions.splice(competitionToDelete, 1);
      setSelectedCompetitions(newCompetitions);
      markDirty();
    }
    setDeleteDialogOpen(false);
    setCompetitionToDelete(null);
  };

  // Reset du formulaire
  const resetForm = () => {
    setNombre('');
    setFechaNacimiento('');
    setAltura('');
    setPeso('');
    setPuesto('');
    setTipo('');
    setSelectedGrupos([]);
    setSelectedImage(null);
    setAvatarPreview(null);
    setNacionalidad('');
    setSegundaNacionalidad('');
    setSeleccionNacional('');
    setJeJiType('');
    setJeJiAttributes([]);
    setMano('');
    setRolOfensivo([]);
    setRolDefensivo([]);
    setPasaporte('');
    setEstatus('');
    setEquipo('');
    setLiga2('');
    setSelectedCompetitions([]);
    setEquipoPreDraft('');
    setAñoDraft('');
    setPosicionDraft('');
    setExperienciaAcb('');
    setNbaExperience('');
    setNivel('');
    setProyeccion('');
    setAgencia('');
    setComparacionPro('');
    setComportamiento('');
    setInjury('');
    setRangoPrecios('');
    setContrato('');
    setUrl('');
    setPit('');
    setEurocamp('');

    setExperiencaEuropa('');
    setShootingRange('');

    setSlVegas('');
    setExperienciaFuera('');
    setExperiencaNcaa('');
    setOrigenGenetico('');
    setBuyout('');

    // On remet isDirty à false
    setIsDirty(false);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseModal = () => {
    setErrorModalOpen(false);
    setErrorMessage('');
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    // Validations minimalistes
    if (!nombre.trim()) {
      setErrorMessage(t('createPlayer.errors.nameRequired'));
      setErrorModalOpen(true);
      return;
    }
    if (!tipo) {
      setErrorMessage(t('createPlayer.errors.typeRequired'));
      setErrorModalOpen(true);
      return;
    }
    if (!fechaNacimiento) {
      setErrorMessage(t('createPlayer.errors.birthDateRequired'));
      setErrorModalOpen(true);
      return;
    }
    if (!altura) {
      setErrorMessage(t('createPlayer.errors.heightRequired'));
      setErrorModalOpen(true);
      return;
    }
    if (!puesto) {
      setErrorMessage(t('createPlayer.errors.positionRequired'));
      setErrorModalOpen(true);
      return;
    }
    if (tipo === 'cantera' && selectedGrupos.length === 0) {
      setErrorMessage(t('createPlayer.errors.canteraGroupRequired'));
      setErrorModalOpen(true);
      return;
    }

    const formData = new FormData();

    formData.append('nom', nombre.trim());
    formData.append('annee_naissance', calcularAñoNacimiento(fechaNacimiento));
    formData.append('date_naissance', fechaNacimiento);
    formData.append('taille', altura || null);
    formData.append('poids', peso || null);
    formData.append('poste', puesto || null);
    formData.append('type', tipo);
    formData.append('section', section);

    // Nationalités
    formData.append('nationalite', nacionalidad || null);
    formData.append('nationalite_2', segundaNacionalidad || null);
    formData.append('selection_national', seleccionNacional || null);

    // Origen Genético - traduit dans la langue du club
    formData.append('origen_genetico', origenGenetico ? t(`createPlayer.geneticOrigins.${origenGenetico}`) : null);

    // JE / JI - attributs traduits dans la langue du club
    formData.append('je_ji', jeJiType || '');
    if (jeJiAttributes.length > 0) {
      const translationKey = jeJiType === 'JE' ? 'jeAttributes' : 'jiAttributes';
      const translatedJeJiAttributes = jeJiAttributes.map(key => t(`createPlayer.${translationKey}.${key}`));
      formData.append('selection_je_ji', translatedJeJiAttributes.join(','));
    } else {
      formData.append('selection_je_ji', '');
    }

    // Mano dominante - traduit dans la langue du club
    formData.append('mano', mano ? t(`createPlayer.fields.${mano}`) : null);
    if (rolOfensivo.length > 0) {
      const translatedOffensiveRoles = rolOfensivo.map(key => t(`createPlayer.offensiveRoles.${key}`));
      formData.append('offensive_role', translatedOffensiveRoles.join(','));
    }
    if (rolDefensivo.length > 0) {
      const translatedDefensiveRoles = rolDefensivo.map(key => t(`createPlayer.defensiveRoles.${key}`));
      formData.append('defensive_role', translatedDefensiveRoles.join(','));
    }

    // Licencia
    formData.append('passport', pasaporte || null);

    formData.append('statut', estatus || null);
    formData.append('team', equipo || '');
    formData.append('league_2', liga2 || null);

    formData.append('pre_draft_team', equipoPreDraft || null);
    formData.append('draft_year', añoDraft || null);
    formData.append('draft_pick', posicionDraft || null);

    // Expériences - traduites dans la langue du club
    formData.append('acb_experience', experienciaAcb ? t(`common.${experienciaAcb}`) : null);
    formData.append('nba_experience', nbaExperience ? t(`common.${nbaExperience}`) : null);

    // Niveau et projection - traduits dans la langue du club
    formData.append('level', nivel ? t(`createPlayer.levels.${nivel}`) : null);
    formData.append('proyeccion', proyeccion ? t(`createPlayer.levels.${proyeccion}`) : null);
    formData.append('agency', agencia || null);
    formData.append('pro_comparaison', comparacionPro || null);
    formData.append('flag_behaviour', comportamiento || null);
    formData.append('injury', injury || null);
    formData.append('digital_behaviour', digitalBehaviour || null);
    formData.append('price_range', rangoPrecios || null);
    formData.append('contract', contrato || null);
    formData.append('url', url || null);
    formData.append('pit', pit || null);
    formData.append('eurocamp', eurocamp || null);

    // SL Vegas
    formData.append('slvegas', slVegas || null);

    // Expériences - traduites dans la langue du club
    formData.append('experienca_europa', experiencaEuropa ? t(`common.${experiencaEuropa}`) : null);
    formData.append('shooting_range', shootingRange || null);

    formData.append('experienca_fuera', experienciaFuera ? t(`common.${experienciaFuera}`) : null);
    formData.append('experienca_ncaa', experiencaNcaa ? t(`common.${experiencaNcaa}`) : null);

    // Buyout
    formData.append('buyout', buyout || null);

    // Categoría - traduit dans la langue du club
    formData.append('categoria', categoria ? t(`createPlayer.fields.${categoria}`) : null);

    // Groupes
    formData.append('groupes', JSON.stringify(selectedGrupos));

    // Competitions (injecter equipe_proprietaire_id si présent)
    const competitionsToSend = selectedCompetitions.map(comp => ({
      ...comp,
      equipe_proprietaire_id: equipeProprietaire ? equipeProprietaire.equipe_id : null
    }));
    formData.append('competitions_detail', JSON.stringify(competitionsToSend));

    // Image
    if (selectedImage) {
      formData.append('avatar', selectedImage);
    }

    setIsSubmitting(true);

    try {
      const response = await api.post('/api/players', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const createdPlayer = response.data;
      const newPlayerId = createdPlayer.playerId;

      setIsSubmitting(false);
      setCreatedPlayerId(newPlayerId);
      setSuccessModalOpen(true);

      // On remet tout à zéro (et isDirty à false)
      resetForm();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setIsSubmitting(false);
      setErrorMessage(error.response?.data?.message || t('createPlayer.errors.createError'));
      setErrorModalOpen(true);
    }
  };

  // Gestion de la capture photo
  const handlePhotoCapture = (file) => {
    setSelectedImage(file);
    setAvatarPreview(URL.createObjectURL(file));
    markDirty();
    setOpenPhotoDialog(false);
  };

  // Gestion de la sélection photo
  const handlePhotoSelect = (file) => {
    setSelectedImage(file);
    setAvatarPreview(URL.createObjectURL(file));
    markDirty();
    setOpenPhotoDialog(false);
  };

  return (
    <Box sx={{
      maxWidth: '100%',
      mx: 'auto',
      mt: 2,
      p: { xs: 2, sm: 3 },
      backgroundColor: (theme) => alpha(theme.palette.background.default, 0.5)
    }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {t('createPlayer.title')}
          </Typography>
          <IconButton
            onClick={() => setTutorialOpen(true)}
            sx={{
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2)
              }
            }}
          >
            <HelpOutline color="primary" />
          </IconButton>
        </Box>
      </Paper>

      {/* Avatar */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            sx={{
              width: 120,
              height: 120,
              border: (theme) => `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}
            src={avatarPreview}
            alt="Avatar"
          />
          <input
            accept="image/*"
            id="avatar-upload"
            type="file"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const maxSize = 1 * 1024 * 1024; // 1 Mo
                if (file.size > maxSize) {
                  setErrorMessage(t('createPlayer.errors.fileTooLarge', 'Le fichier est trop volumineux. Taille maximale : 1 Mo'));
                  setErrorModalOpen(true);
                  e.target.value = '';
                  return;
                }
                handlePhotoSelect(file);
              }
            }}
            style={{ display: 'none' }}
          />
          <label htmlFor="avatar-upload">
            <IconButton
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: 'primary.main',
                color: 'white',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
              component="span"
              size="small"
            >
              <PhotoCamera fontSize="small" />
            </IconButton>
          </label>
        </Box>
      </Box>

      {/* 1. INFORMACIÓN BÁSICA */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            pb: 1.5,
            fontWeight: 600,
            borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
            display: 'inline-block'
          }}
        >
          {t('createPlayer.sections.basicInfo')}
        </Typography>

        {/* Ligne 1 : Nombre - Tipo */}
        <Box
          sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)'
          }
        }}
      >
        {/* Nombre */}
        <TextField
          label={t('createPlayer.fields.name')}
          fullWidth
          value={nombre}
          onChange={(e) => {
            setNombre(e.target.value);
            markDirty();
          }}
          error={nombre.trim() === ''}
          helperText={nombre.trim() === '' ? t('createPlayer.fields.nameRequired') : ''}
          inputProps={{ style: { textTransform: 'uppercase' } }}
        />

        {/* Grupo (= Tipo) */}
        <FormControl fullWidth error={!tipo}>
          <InputLabel>{t('createPlayer.fields.group')}</InputLabel>
          <Select
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.group')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setTipo('');
                  markDirty();
                }}
                sx={{
                  visibility: tipo ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="pro">{t('createPlayer.fields.pro')}</MenuItem>
            <MenuItem value="cantera">{t('createPlayer.fields.cantera')}</MenuItem>
          </Select>
          {!tipo && (
            <FormHelperText>{t('createPlayer.fields.groupRequired')}</FormHelperText>
          )}
        </FormControl>
      </Box>

      {/* Sélecteur de groupes (multiple) */}
      <FormControl
        fullWidth
        sx={{ mt: 2 }}
        error={tipo === 'cantera' && selectedGrupos.length === 0}
      >
        <InputLabel>{t('createPlayer.fields.groups')}</InputLabel>
        <Select
          multiple
          value={selectedGrupos}
          onChange={(e) => {
            setSelectedGrupos(e.target.value);
            markDirty();
          }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((id) => {
                const grupo = grupos.find((g) => g.id === id);
                return (
                  <Chip
                    key={id}
                    label={grupo ? grupo.libelle : ''}
                    sx={{
                      backgroundColor: grupo ? grupo.code_couleur : 'grey',
                      color: '#fff'
                    }}
                  />
                );
              })}
            </Box>
          )}
          endAdornment={
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedGrupos([]);
                markDirty();
              }}
              sx={{
                visibility: selectedGrupos.length > 0 ? 'visible' : 'hidden',
                position: 'absolute',
                right: 32
              }}
              size="small"
            >
              <Clear fontSize="small" />
            </IconButton>
          }
        >
          {grupos.map((grupo) => (
            <MenuItem key={grupo.id} value={grupo.id}>
              <Checkbox checked={selectedGrupos.indexOf(grupo.id) > -1} />
              <ListItemText primary={grupo.libelle} />
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: grupo.code_couleur,
                  borderRadius: '50%',
                  marginLeft: 1,
                }}
              />
            </MenuItem>
          ))}
        </Select>
        {tipo === 'cantera' && selectedGrupos.length === 0 && (
          <FormHelperText>{t('createPlayer.fields.groupsRequired')}</FormHelperText>
        )}
      </FormControl>

      {/* Ligne 2 : Peso - Altura */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: '1fr 1fr 1fr'
          }
        }}
      >
        {/* Peso (kg) */}
        <TextField
          label={t('createPlayer.fields.weight')}
          type="number"
          fullWidth
          value={peso}
          onChange={(e) => {
            setPeso(e.target.value);
            markDirty();
          }}
        />

        {/* Altura (cm) */}
        <TextField
          label={t('createPlayer.fields.height')}
          type="number"
          fullWidth
          value={altura}
          onChange={(e) => {
            setAltura(e.target.value);
            markDirty();
          }}
          error={!altura}
          helperText={!altura ? t('createPlayer.fields.heightRequired') : ''}
        />
      </Box>

      {/* Ligne 3 : Nacionalidad / 2a Nacionalidad / Origen Genético */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr 1fr'
          }
        }}
      >
        <Box>
          <NationalitiesSelect 
            value={nacionalidad}
            onChange={(e) => {
              setNacionalidad(e.target.value);
              markDirty();
            }}
          />
        </Box>

        <Box>
          <NationalitiesSelect2 
            value={segundaNacionalidad}
            onChange={(e) => {
              setSegundaNacionalidad(e.target.value);
              markDirty();
            }}
          />
        </Box>

        {/* *** Origen Genético *** */}
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.geneticOrigin')}</InputLabel>
          <Select
            value={origenGenetico}
            onChange={(e) => {
              setOrigenGenetico(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.geneticOrigin')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setOrigenGenetico('');
                  markDirty();
                }}
                sx={{
                  visibility: origenGenetico ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            {GENETIC_ORIGIN_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                {t(`createPlayer.geneticOrigins.${key}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Ligne 4 : Fecha de nacimiento + Licencia */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr'
          }
        }}
      >
        {/* Fecha de Nacimiento */}
        <TextField
          label={t('createPlayer.fields.birthDate')}
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={fechaNacimiento}
          onChange={(e) => {
            setFechaNacimiento(e.target.value);
            markDirty();
          }}
          error={!fechaNacimiento}
          helperText={!fechaNacimiento ? t('createPlayer.fields.birthDateRequired') : ''}
        />

        {/* Licencia */}
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.license')}</InputLabel>
          <Select
            value={pasaporte}
            onChange={(e) => {
              setPasaporte(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.license')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setPasaporte('');
                  markDirty();
                }}
                sx={{
                  visibility: pasaporte ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="EXT">EXT</MenuItem>
            <MenuItem value="EUR">EUR</MenuItem>
            <MenuItem value="JFL">JFL</MenuItem>
            <MenuItem value="COT">COT</MenuItem>
          </Select>
        </FormControl>
        </Box>
      </Box>

      {/* 2. CARACTERÍSTICAS DEL JUGADOR */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            pb: 1.5,
            fontWeight: 600,
            borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
            display: 'inline-block'
          }}
        >
          {t('createPlayer.sections.playerCharacteristics')}
        </Typography>

      {/* Fila 1 : Posición - Mano Dominante - Shooting Range */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, 1fr)',
          }
        }}
      >
        {/* Posición */}
        <FormControl fullWidth error={!puesto}>
          <InputLabel>{t('createPlayer.fields.position')}</InputLabel>
          <Select
            value={puesto}
            onChange={(e) => {
              setPuesto(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.position')}
            error={!puesto}
            endAdornment={
              <IconButton
                onClick={(ev) => {
                  ev.stopPropagation();
                  setPuesto('');
                  markDirty();
                }}
                sx={{
                  visibility: puesto ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            {positionsWithKeys.map(({ key, label }) => (
              <MenuItem key={key} value={label}>{label}</MenuItem>
            ))}
          </Select>
          {!puesto && (
            <FormHelperText>{t('createPlayer.fields.positionRequired')}</FormHelperText>
          )}
        </FormControl>

        {/* Mano dominante */}
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.dominantHand')}</InputLabel>
          <Select
            value={mano}
            onChange={(e) => {
              setMano(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.dominantHand')}
            endAdornment={
              <IconButton
                onClick={(ev) => {
                  ev.stopPropagation();
                  setMano('');
                  markDirty();
                }}
                sx={{
                  visibility: mano ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="rightHand">{t('createPlayer.fields.rightHand')}</MenuItem>
            <MenuItem value="leftHand">{t('createPlayer.fields.leftHand')}</MenuItem>
            <MenuItem value="bothHands">{t('createPlayer.fields.bothHands')}</MenuItem>
          </Select>
        </FormControl>

        {/* Shooting Range */}
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.shootingRange')}</InputLabel>
          <Select
            value={shootingRange}
            onChange={(e) => {
              setShootingRange(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.shootingRange')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setShootingRange('');
                  markDirty();
                }}
                sx={{
                  visibility: shootingRange ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="Rim">Rim</MenuItem>
            <MenuItem value="Mid-Range">Mid-Range</MenuItem>
            <MenuItem value="3Pts">3Pts</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Categoría */}
      <Box sx={{ mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.category')}</InputLabel>
          <Select
            value={categoria}
            onChange={(e) => {
              setCategoria(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.category')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setCategoria('');
                  markDirty();
                }}
                sx={{
                  visibility: categoria ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="handler">{t('createPlayer.fields.handler')}</MenuItem>
            <MenuItem value="exterior">{t('createPlayer.fields.exterior')}</MenuItem>
            <MenuItem value="interior">{t('createPlayer.fields.interior')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Fila 2 : Perfil Ofensivo - Perfil Defensivo */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr'
          }
        }}
      >
        {/* Perfil Ofensivo */}
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.offensiveProfile')}</InputLabel>
          <Select
            multiple
            value={rolOfensivo}
            onChange={(e) => {
              setRolOfensivo(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.offensiveProfile')}
            renderValue={(selected) => selected.map(key => t(`createPlayer.offensiveRoles.${key}`)).join(', ')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setRolOfensivo([]);
                  markDirty();
                }}
                sx={{ 
                  visibility: rolOfensivo.length > 0 ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            {/* Guard */}
            <MenuItem disabled divider sx={{ pointerEvents: 'none', fontWeight: 600 }}>
              {t('createPlayer.offensiveRoles.guard')}
            </MenuItem>
            {OFFENSIVE_ROLE_KEYS.guard.map((key) => (
              <MenuItem key={key} value={key}>{t(`createPlayer.offensiveRoles.${key}`)}</MenuItem>
            ))}

            {/* Wing */}
            <MenuItem disabled divider sx={{ pointerEvents: 'none', fontWeight: 600 }}>
              {t('createPlayer.offensiveRoles.wing')}
            </MenuItem>
            {OFFENSIVE_ROLE_KEYS.wing.map((key) => (
              <MenuItem key={key} value={key}>{t(`createPlayer.offensiveRoles.${key}`)}</MenuItem>
            ))}

            {/* Forward */}
            <MenuItem disabled divider sx={{ pointerEvents: 'none', fontWeight: 600 }}>
              {t('createPlayer.offensiveRoles.forward')}
            </MenuItem>
            {OFFENSIVE_ROLE_KEYS.forward.map((key) => (
              <MenuItem key={key} value={key}>{t(`createPlayer.offensiveRoles.${key}`)}</MenuItem>
            ))}

            {/* Big */}
            <MenuItem disabled divider sx={{ pointerEvents: 'none', fontWeight: 600 }}>
              {t('createPlayer.offensiveRoles.big')}
            </MenuItem>
            {OFFENSIVE_ROLE_KEYS.big.map((key) => (
              <MenuItem key={key} value={key}>{t(`createPlayer.offensiveRoles.${key}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Perfil Defensivo */}
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.defensiveProfile')}</InputLabel>
          <Select
            multiple
            value={rolDefensivo}
            onChange={(e) => {
              setRolDefensivo(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.defensiveProfile')}
            renderValue={(selected) => selected.map(key => t(`createPlayer.defensiveRoles.${key}`)).join(', ')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setRolDefensivo([]);
                  markDirty();
                }}
                sx={{ 
                  visibility: rolDefensivo.length > 0 ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            {DEFENSIVE_ROLE_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={rolDefensivo.indexOf(key) > -1} />
                <ListItemText primary={t(`createPlayer.defensiveRoles.${key}`)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Fila 3 : Skills JE/JI - Skills JE ou JI */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr'
          }
        }}
      >
        {/* JE / JI */}
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.skillsJeJi')}</InputLabel>
          <Select
            value={jeJiType}
            onChange={(e) => {
              setJeJiType(e.target.value);
              setJeJiAttributes([]);
              markDirty();
            }}
            label={t('createPlayer.fields.skillsJeJi')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setJeJiType('');
                  setJeJiAttributes([]);
                  markDirty();
                }}
                sx={{ 
                  visibility: jeJiType ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="JE">{t('createPlayer.fields.jeExterior')}</MenuItem>
            <MenuItem value="JI">{t('createPlayer.fields.jiInterior')}</MenuItem>
          </Select>
        </FormControl>

        {/* Atributos JE/JI */}
        {jeJiType && (
          <FormControl fullWidth>
            <InputLabel>{t('createPlayer.fields.skillsJeOrJi')}</InputLabel>
            <Select
              multiple
              value={jeJiAttributes}
              onChange={(e) => {
                setJeJiAttributes(e.target.value);
                markDirty();
              }}
              renderValue={(selected) => selected.map(key => t(`createPlayer.${jeJiType === 'JE' ? 'jeAttributes' : 'jiAttributes'}.${key}`)).join(', ')}
              label={t('createPlayer.fields.skillsJeOrJi')}
              endAdornment={
                <IconButton
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setJeJiAttributes([]);
                    markDirty();
                  }}
                  sx={{
                    visibility: jeJiAttributes.length > 0 ? 'visible' : 'hidden',
                    position: 'absolute',
                    right: 32
                  }}
                  size="small"
                >
                  <Clear fontSize="small" />
                </IconButton>
              }
            >
              {(jeJiType === 'JE' ? JE_ATTRIBUTE_KEYS : JI_ATTRIBUTE_KEYS).map((key) => (
                <MenuItem key={key} value={key}>
                  <Checkbox checked={jeJiAttributes.indexOf(key) > -1} />
                  <ListItemText primary={t(`createPlayer.${jeJiType === 'JE' ? 'jeAttributes' : 'jiAttributes'}.${key}`)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Fila 4 : Current VBC Rating - Proyección VBC Rating */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr'
          }
        }}
      >
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.currentVbcRating', { clubName })}</InputLabel>
          <Select
            value={nivel}
            onChange={(e) => {
              setNivel(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.currentVbcRating', { clubName })}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setNivel('');
                  markDirty();
                }}
                sx={{ 
                  visibility: nivel ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            {VBC_LEVEL_KEYS.map((key) => (
              <MenuItem key={key} value={key}>{t(`createPlayer.levels.${key}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.projectionVbcRating', { clubName })}</InputLabel>
          <Select
            value={proyeccion}
            onChange={(e) => {
              setProyeccion(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.projectionVbcRating', { clubName })}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setProyeccion('');
                  markDirty();
                }}
                sx={{
                  visibility: proyeccion ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            {VBC_LEVEL_KEYS.filter(key => !key.includes('Prospect') && key !== 'futureStar').map((key) => (
              <MenuItem key={key} value={key}>{t(`createPlayer.levels.${key}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Fila 5 : Flag Behaviour - Injury Prone */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr'
          }
        }}
      >
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.flagBehaviour')}</InputLabel>
          <Select
            value={comportamiento}
            onChange={(e) => {
              setComportamiento(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.flagBehaviour')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setComportamiento('');
                  markDirty();
                }}
                sx={{ 
                  visibility: comportamiento ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="Green">{t('createPlayer.fields.green')}</MenuItem>
            <MenuItem value="Orange">{t('createPlayer.fields.orange')}</MenuItem>
            <MenuItem value="Red">{t('createPlayer.fields.red')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.injuryProne')}</InputLabel>
          <Select
            value={injury}
            onChange={(e) => {
              setInjury(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.injuryProne')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setInjury('');
                  markDirty();
                }}
                sx={{
                  visibility: injury ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="Green">{t('createPlayer.fields.green')}</MenuItem>
            <MenuItem value="Orange">{t('createPlayer.fields.orange')}</MenuItem>
            <MenuItem value="Red">{t('createPlayer.fields.red')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.digitalBehaviour')}</InputLabel>
          <Select
            value={digitalBehaviour}
            onChange={(e) => {
              setDigitalBehaviour(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.digitalBehaviour')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setDigitalBehaviour('');
                  markDirty();
                }}
                sx={{
                  visibility: digitalBehaviour ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="Green">{t('createPlayer.fields.green')}</MenuItem>
            <MenuItem value="Orange">{t('createPlayer.fields.orange')}</MenuItem>
            <MenuItem value="Red">{t('createPlayer.fields.red')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Fila 6 : Comparación con un Jugador Pro */}
      <Box sx={{ mt: 2 }}>
        <TextField
          label={t('createPlayer.fields.proComparison')}
          fullWidth
          value={comparacionPro}
          onChange={(e) => {
            setComparacionPro(e.target.value);
            markDirty();
          }}
        />
      </Box>
      </Box>

      {/* 3. SITUACIÓN ACTUAL */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            pb: 1.5,
            fontWeight: 600,
            borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
            display: 'inline-block'
          }}
        >
          {t('createPlayer.sections.currentSituation')}
        </Typography>

      {/* Fila 1 : Selección Nacional */}
      <Box sx={{ mb: 2 }}>
        <SelectSelectionNational
          value={seleccionNacional}
          onChange={(e) => {
            setSeleccionNacional(e.target.value);
            markDirty();
          }}
        />
      </Box>

      {/* Nivel / Competición / Equipo */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            {t('createPlayer.fields.levelCompetitionTeam')}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={handleOpenDialog}
          >
            {t('createPlayer.buttons.add')}
          </Button>
        </Box>

        {selectedCompetitions.length > 0 ? (
          <Grid container spacing={1}>
            {selectedCompetitions.map((comp, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.default'
                }}>
                  <Box
                    component="img"
                    src={getLogoUrl(comp.competition_id, 'competitions')}
                    alt={comp.competition_nom}
                    sx={{
                      width: 35,
                      height: 35,
                      objectFit: 'contain',
                      mr: 1.5,
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <Box
                    component="img"
                    src={getLogoUrl(comp.equipe_id, 'equipes')}
                    alt={comp.equipe_nom}
                    sx={{
                      width: 35,
                      height: 35,
                      objectFit: 'contain',
                      mr: 1.5,
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      label={comp.niveau_nom}
                      size="small"
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        mb: 0.5
                      }}
                    />
                    <Typography variant="body2">
                      {comp.competition_nom}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {comp.equipe_nom}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleRemoveCompetition(index, e)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {t('createPlayer.fields.noCompetitionAssigned')}
          </Typography>
        )}

        {/* Equipe propriétaire (cedido) */}
        {selectedCompetitions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {t('createPlayer.fields.ownerTeam')}
              </Typography>
              {!equipeProprietaire && (
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => { setOwnerSearchTerm(''); setOwnerDialogOpen(true); }}
                >
                  {t('createPlayer.buttons.add')}
                </Button>
              )}
            </Box>
            {equipeProprietaire ? (
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.default'
                  }}>
                    <Box
                      component="img"
                      src={getLogoUrl(equipeProprietaire.equipe_id, 'equipes')}
                      alt={equipeProprietaire.equipe_nom}
                      sx={{ width: 35, height: 35, objectFit: 'contain', mr: 1.5, borderRadius: '4px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Chip
                        label={t('createPlayer.fields.ownerTeamBadge')}
                        size="small"
                        sx={{ backgroundColor: '#ff9800', color: 'white', mb: 0.5 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {equipeProprietaire.equipe_nom}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {equipeProprietaire.competitions?.map(c => c.competition_nom).join(', ')}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setEquipeProprietaire(null)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {t('createPlayer.fields.ownerTeamHelper')}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Equipo Actual - Campo adicional para el campo team de la tabla players */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
          {t('createPlayer.fields.currentTeam')}
        </Typography>
        <Autocomplete
          freeSolo
          options={teams}
          inputValue={equipo}
          onInputChange={(event, newInputValue) => {
            setEquipo(newInputValue);
            markDirty();
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('createPlayer.fields.currentTeam')}
              fullWidth
            />
          )}
        />
      </Box>
      </Box>

      {/* 4. EXPERIENCIA PROFESIONAL */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            pb: 1.5,
            fontWeight: 600,
            borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
            display: 'inline-block'
          }}
        >
          {t('createPlayer.sections.professionalExperience')}
        </Typography>

      {/* Fila 1 : Año del Draft - Posición en el Draft - Equipo Pre-Draft */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, 1fr)'
          }
        }}
      >
        <TextField
          label={t('createPlayer.fields.draftYear')}
          type="number"
          fullWidth
          value={añoDraft}
          onChange={(e) => {
            setAñoDraft(e.target.value);
            markDirty();
          }}
        />

        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.draftPosition')}</InputLabel>
          <Select
            value={posicionDraft}
            onChange={(e) => {
              setPosicionDraft(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.draftPosition')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setPosicionDraft('');
                  markDirty();
                }}
                sx={{ 
                  visibility: posicionDraft ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            {/* 1st Round */}
            <MenuItem disabled divider sx={{ pointerEvents: 'none', fontWeight: 600 }}>
              1st Round
            </MenuItem>
            {Array.from({ length: 30 }, (_, i) => i + 1).map((pick) => (
              <MenuItem key={`1st-${pick}`} value={`1st Round, #${pick}`}>
                1st Round, #{pick}
              </MenuItem>
            ))}
            {/* 2nd Round */}
            <MenuItem disabled divider sx={{ pointerEvents: 'none', fontWeight: 600 }}>
              2nd Round
            </MenuItem>
            {Array.from({ length: 30 }, (_, i) => i + 31).map((pick) => (
              <MenuItem key={`2nd-${pick}`} value={`2nd Round, #${pick}`}>
                2nd Round, #{pick}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          freeSolo
          options={teams}
          inputValue={equipoPreDraft}
          onInputChange={(event, newInputValue) => {
            setEquipoPreDraft(newInputValue);
            markDirty();
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('createPlayer.fields.preDraftTeam')}
              fullWidth
            />
          )}
        />
      </Box>

      {/* Fila 2 : PIT - Eurocamp - SL Vegas */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, 1fr)'
          }
        }}
      >
        <TextField
          label="PIT"
          type="number"
          fullWidth
          value={pit}
          onChange={(e) => {
            setPit(e.target.value);
            markDirty();
          }}
        />

        <YearChips
          label={t('createPlayer.fields.eurocamp')}
          value={eurocamp}
          onChange={(newValue) => {
            setEurocamp(newValue);
            markDirty();
          }}
          helperText={t('createPlayer.fields.yearsHelperText')}
        />

        <YearChips
          label={t('createPlayer.fields.slVegas')}
          value={slVegas}
          onChange={(newValue) => {
            setSlVegas(newValue);
            markDirty();
          }}
          helperText={t('createPlayer.fields.yearsHelperText')}
        />
      </Box>

      {/* Fila 3 : Experiencia NBA - Experiencia ACB - Experiencia Europa - Experiencia Fuera - Experiencia NCAA */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(5, 1fr)'
          }
        }}
      >
        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.nbaExperience')}</InputLabel>
          <Select
            value={nbaExperience}
            onChange={(e) => {
              setNbaExperience(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.nbaExperience')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setNbaExperience('');
                  markDirty();
                }}
                sx={{ 
                  visibility: nbaExperience ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="yes">{t('common.yes')}</MenuItem>
            <MenuItem value="no">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.acbExperience')}</InputLabel>
          <Select
            value={experienciaAcb}
            onChange={(e) => {
              setExperienciaAcb(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.acbExperience')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setExperienciaAcb('');
                  markDirty();
                }}
                sx={{
                  visibility: experienciaAcb ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="yes">{t('common.yes')}</MenuItem>
            <MenuItem value="no">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.europeExperience')}</InputLabel>
          <Select
            value={experiencaEuropa}
            onChange={(e) => {
              setExperiencaEuropa(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.europeExperience')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setExperiencaEuropa('');
                  markDirty();
                }}
                sx={{
                  visibility: experiencaEuropa ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="yes">{t('common.yes')}</MenuItem>
            <MenuItem value="no">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.abroadExperience')}</InputLabel>
          <Select
            value={experienciaFuera}
            onChange={(e) => {
              setExperienciaFuera(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.abroadExperience')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setExperienciaFuera('');
                  markDirty();
                }}
                sx={{
                  visibility: experienciaFuera ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="yes">{t('common.yes')}</MenuItem>
            <MenuItem value="no">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{t('createPlayer.fields.ncaaExperience')}</InputLabel>
          <Select
            value={experiencaNcaa}
            onChange={(e) => {
              setExperiencaNcaa(e.target.value);
              markDirty();
            }}
            label={t('createPlayer.fields.ncaaExperience')}
            endAdornment={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setExperiencaNcaa('');
                  markDirty();
                }}
                sx={{
                  visibility: experiencaNcaa ? 'visible' : 'hidden',
                  position: 'absolute',
                  right: 32
                }}
                size="small"
              >
                <Clear fontSize="small" />
              </IconButton>
            }
          >
            <MenuItem value="yes">{t('common.yes')}</MenuItem>
            <MenuItem value="no">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>
      </Box>
      </Box>

      {/* 5. INFORMACIÓN CONTRACTUAL */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            pb: 1.5,
            fontWeight: 600,
            borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
            display: 'inline-block'
          }}
        >
          {t('createPlayer.sections.contractInfo')}
        </Typography>

      {/* Fila 1 : Agencia - Rango Salarial - Contrato */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, 1fr)'
          }
        }}
      >
        {/* Agencia */}
        <Autocomplete
          freeSolo
          options={agencies}
          inputValue={agencia}
          onInputChange={(event, newInputValue) => {
            setAgencia(newInputValue);
            markDirty();
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('createPlayer.fields.agency')}
              fullWidth
            />
          )}
        />

        {/* Rango Salarial */}
        <Autocomplete
          freeSolo
          options={priceRanges}
          inputValue={rangoPrecios}
          onInputChange={(event, newInputValue) => {
            setRangoPrecios(newInputValue);
            markDirty();
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('createPlayer.fields.salaryRange')}
              fullWidth
            />
          )}
        />

        {/* Contrato */}
        <Autocomplete
          freeSolo
          options={contratos}
          inputValue={contrato}
          onInputChange={(event, newInputValue) => {
            setContrato(newInputValue);
            markDirty();
          }}
          getOptionLabel={(option) => t(`createPlayer.contractDurations.${option}`, option)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('createPlayer.fields.contract')}
              fullWidth
            />
          )}
        />
      </Box>

      {/* Fila 2 : Buyout */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mt: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr'
          }
        }}
      >
        {/* Buyout */}
        <Autocomplete
          freeSolo
          options={buyoutOptions}
          inputValue={buyout}
          onInputChange={(event, newInputValue) => {
            setBuyout(newInputValue);
            markDirty();
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('createPlayer.fields.buyout')}
              fullWidth
            />
          )}
        />
      </Box>
      </Box>

      {/* 6. INFORMACIÓN ADICIONAL */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            pb: 1.5,
            fontWeight: 600,
            borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
            display: 'inline-block'
          }}
        >
          {t('createPlayer.sections.additionalInfo')}
        </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr'
          }
        }}
      >
        {/* URL Estadística */}
        <TextField
          label={t('createPlayer.fields.statisticsUrl')}
          fullWidth
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            markDirty();
          }}
        />
      </Box>
      </Box>

      {/* Boutons */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
        {isSubmitting && <CircularProgress size={24} />}
        <Button
          variant="outlined"
          color="primary"
          onClick={() => window.history.back()}
          disabled={isSubmitting}
        >
          {t('createPlayer.buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('createPlayer.buttons.creating') : t('createPlayer.buttons.createPlayer')}
        </Button>
      </Box>

      {/* Modal tutorial */}
      <Dialog open={tutorialOpen} onClose={() => setTutorialOpen(false)} maxWidth="md">
        <DialogTitle>{t('createPlayer.tutorial.title')}</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            {t('createPlayer.tutorial.welcome')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('createPlayer.tutorial.requiredFields')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('createPlayer.tutorial.canteraNote')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('createPlayer.tutorial.photoNote')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('createPlayer.tutorial.optionalFields')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTutorialOpen(false)}>{t('createPlayer.tutorial.understood')}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal d'erreur */}
      <Dialog open={errorModalOpen} onClose={handleCloseModal}>
        <DialogTitle>{t('createPlayer.dialogs.error')}</DialogTitle>
        <DialogContent>
          <Typography>{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            {t('createPlayer.buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de succès */}
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box sx={{ textAlign: 'center', pt: 4, pb: 2, px: 3, background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)' }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: '#fff', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
            {t('createPlayer.dialogs.success')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
            {t('createPlayer.dialogs.successMessage')}
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>
            {t('createPlayer.buttons.createReport')}
          </Typography>
          <Grid container spacing={1}>
            {[
              { type: 'informe', icon: <AssignmentIcon />, label: t('createPlayer.dialogs.report'), color: '#4caf50' },
              { type: 'intel', icon: <LightbulbIcon />, label: t('createPlayer.dialogs.intel'), color: '#ff9800' },
              { type: 'techniques', icon: <DescriptionIcon />, label: t('createPlayer.dialogs.technicalSheet'), color: '#2196f3' },
              { type: 'video', icon: <VideoLibraryIcon />, label: t('createPlayer.dialogs.videos'), color: '#9c27b0' },
              { type: 'medical', icon: <MedicalIcon />, label: t('createPlayer.dialogs.medical'), color: '#f44336' },
              { type: 'medidas', icon: <StraightenIcon />, label: t('createPlayer.dialogs.measures'), color: '#00bcd4' },
              { type: 'documents', icon: <FolderOpenIcon />, label: t('createPlayer.dialogs.documents'), color: '#795548' },
            ].map(item => (
              <Grid item xs={6} sm={4} key={item.type}>
                <Paper
                  elevation={0}
                  onClick={() => handleMenuItemClick(item.type)}
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: item.color, bgcolor: alpha(item.color, 0.05), transform: 'translateY(-2px)', boxShadow: 2 }
                  }}
                >
                  <Box sx={{ color: item.color, display: 'flex' }}>{item.icon}</Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', lineHeight: 1.2 }}>{item.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => { setSuccessModalOpen(false); resetForm(); }}
            variant="outlined"
            startIcon={<PersonAddIcon />}
            sx={{ flex: 1, borderRadius: 2 }}
          >
            {t('createPlayer.buttons.createAnother')}
          </Button>
          <Button
            onClick={() => window.history.back()}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ flex: 1, borderRadius: 2 }}
          >
            {t('createPlayer.buttons.goBack')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour sélection des compétitions avec filtre */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">{t('createPlayer.dialogs.selectTeam')}</Typography>
          <TextField
            fullWidth
            placeholder={t('createPlayer.dialogs.searchTeam')}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            <List dense>
              {getFilteredEquipes().map(equipeOption => {
                // Vérifier si toutes les compétitions de cette équipe sont déjà sélectionnées
                const allSelected = equipeOption.competitions.every(competition =>
                  selectedCompetitions.find(c =>
                    c.niveau_id === competition.niveau_id &&
                    c.competition_id === competition.competition_id &&
                    c.equipe_id === equipeOption.equipe_id
                  )
                );

                return (
                  <ListItemButton
                    key={equipeOption.equipe_id}
                    onClick={() => !allSelected && handleAddEquipe(equipeOption)}
                    disabled={allSelected}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1,
                      '&:hover': {
                        backgroundColor: allSelected ? 'transparent' : 'action.hover'
                      },
                      cursor: allSelected ? 'default' : 'pointer'
                    }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(equipeOption.equipe_id, 'equipes')}
                      alt={equipeOption.equipe_nom}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        mr: 2,
                        borderRadius: '4px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {equipeOption.equipe_nom}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ({equipeOption.competitions.map(c => c.competition_nom).join(', ')})
                          </Typography>
                        </Box>
                      }
                    />
                    {allSelected ? (
                      <Chip label="✓" size="small" color="success" />
                    ) : (
                      <AddIcon color="primary" />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          {/* Afficher les compétitions sélectionnées */}
          {selectedCompetitions.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                {t('createPlayer.dialogs.selectedCompetitions')} ({selectedCompetitions.length}):
              </Typography>
              <Grid container spacing={1}>
                {selectedCompetitions.map((comp, index) => (
                  <Grid item key={index}>
                    <Chip
                      label={`${comp.niveau_nom} - ${comp.competition_nom} - ${comp.equipe_nom}`}
                      onDelete={() => {
                        const newComps = [...selectedCompetitions];
                        newComps.splice(index, 1);
                        setSelectedCompetitions(newComps);
                        markDirty();
                      }}
                      variant="outlined"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('createPlayer.buttons.cancel')}</Button>
          <Button
            onClick={handleSaveCompetitions}
            variant="contained"
            disabled={selectedCompetitions.length === 0}
          >
            {t('createPlayer.buttons.save')} ({selectedCompetitions.length} {t('createPlayer.dialogs.selected')})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('createPlayer.dialogs.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('createPlayer.dialogs.confirmDeleteMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('createPlayer.buttons.cancel')}</Button>
          <Button onClick={confirmDeleteCompetition} color="error" variant="contained">
            {t('createPlayer.buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour sélection de l'équipe propriétaire */}
      <Dialog open={ownerDialogOpen} onClose={() => setOwnerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">{t('createPlayer.fields.ownerTeam')}</Typography>
          <TextField
            fullWidth
            placeholder={t('createPlayer.dialogs.searchTeam')}
            variant="outlined"
            value={ownerSearchTerm}
            onChange={(e) => setOwnerSearchTerm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            <List dense>
              {equipesOptions
                .filter(eq => !selectedCompetitions.some(c => c.equipe_id === eq.equipe_id))
                .filter(eq => !ownerSearchTerm || eq.equipe_nom.toLowerCase().includes(ownerSearchTerm.toLowerCase()))
                .map(equipeOption => (
                  <ListItemButton
                    key={equipeOption.equipe_id}
                    onClick={() => {
                      setEquipeProprietaire(equipeOption);
                      setOwnerDialogOpen(false);
                    }}
                    selected={equipeProprietaire?.equipe_id === equipeOption.equipe_id}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1
                    }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(equipeOption.equipe_id, 'equipes')}
                      alt={equipeOption.equipe_nom}
                      sx={{ width: 40, height: 40, objectFit: 'contain', mr: 2, borderRadius: '4px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {equipeOption.equipe_nom}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ({equipeOption.competitions.map(c => c.competition_nom).join(', ')})
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOwnerDialogOpen(false)}>{t('createPlayer.buttons.cancel')}</Button>
          {equipeProprietaire && (
            <Button onClick={() => { setEquipeProprietaire(null); setOwnerDialogOpen(false); }} color="error">
              {t('createPlayer.buttons.delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrearJugador;
